import {
  buildPosologySummary,
  buildPrescriptionDocumentData,
  createPrintTranslator,
  type AddressData,
  type PrescriptionDocumentData,
  type PrescriptionSourceRecord,
} from "@vet/print-core";
import type { MedicalPrescriptionInput, PrescriptionItemInput } from "@/lib/validations/medical-prescription";

export type ItemKind = "medication" | "formula" | "exam";
type Lang = "pt" | "en";

export interface DraftContext {
  pet: {
    name: string;
    birth_date: string | null;
    species: string | null;
    breed: string | null;
    gender: string | null;
    weights: Array<{
      weight: number | null;
      measured_at: string | null;
      medical_record_id: string | null;
      deleted_at: string | null;
    }>;
    owner: { name: string | null; document: string | null; addresses: AddressData[] } | null;
  } | null;
  vet: { name: string; crmvForPrint: string; phone: string | null; address: string | null; mapa: string | null };
  clinic: { name: string | null; cnpj: string | null };
  medicalRecordId: string | null;
}

export interface DraftHighlight {
  kind: ItemKind;
  index: number;
}

const FRACTIONS: Record<string, number> = { "½": 0.5, "¼": 0.25, "¾": 0.75, "⅓": 1 / 3, "⅔": 2 / 3 };

/** "2,5" → 2.5, "½" → 0.5, "1/2" → 0.5, "1 1/2" → 1.5. */
export function parseLocaleNumber(value: string | null | undefined): number | null {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return null;
  if (FRACTIONS[raw] != null) return FRACTIONS[raw];
  const mixed = raw.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) return Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
  const fraction = raw.match(/^(\d+)\/(\d+)$/);
  if (fraction) return Number(fraction[2]) === 0 ? null : Number(fraction[1]) / Number(fraction[2]);
  const normalized = raw.replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;
  return Number(normalized);
}

/** Same sentence the printed document uses (single source: print-core). */
export function buildPosologySentence(item: PrescriptionItemInput, lang: Lang): string {
  const t = createPrintTranslator(lang === "pt" ? "pt-BR" : "en");
  return buildPosologySummary(item, t, lang);
}

const joinNonEmpty = (parts: Array<string | null | undefined>, sep: string) =>
  parts.map((p) => (p ?? "").trim()).filter(Boolean).join(sep);

export function summarizeItem(item: PrescriptionItemInput, lang: Lang): { title: string; summary: string } {
  const title = joinNonEmpty([item.medication_name, item.medication_concentration, item.pharmaceutical_form], " · ");
  let summary: string;
  if (item.posology_mode === "free_text") {
    summary = (item.free_text_posology ?? "").trim().split("\n")[0] ?? "";
  } else {
    const dose = joinNonEmpty([item.dose_amount, item.dose_unit], " ");
    const every = item.frequency_value ? joinNonEmpty([lang === "pt" ? "a cada" : "every", item.frequency_value, item.frequency_unit], " ") : "";
    const span = item.duration_value ? joinNonEmpty([item.duration_value, item.duration_unit], " ") : "";
    summary = joinNonEmpty([dose, item.administration_route, every, span], " · ");
  }
  return { title, summary };
}

/** "Nome — instruções" (also accepts " - ") → one exam per non-empty line. */
export function parseExamLines(text: string): Array<{ exam_name: string; instructions: string }> {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = line.match(/^(.*?)\s+[—-]\s+(.*)$/);
      return match
        ? { exam_name: match[1].trim(), instructions: match[2].trim() }
        : { exam_name: line, instructions: "" };
    });
}

const todayIso = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Builds the document model for the live preview through the same mapper the PDF uses. */
export function buildDraftDocumentData(args: {
  values: MedicalPrescriptionInput;
  examItems?: Array<{ exam_name: string; instructions?: string | null }>;
  context: DraftContext;
}): PrescriptionDocumentData {
  const { values, examItems = [], context } = args;
  const type = values.type;
  const items = type === "manipulated" ? [] : (values.items ?? []);
  const record: PrescriptionSourceRecord = {
    type: (values.type as string) === "exam" || (examItems.length > 0 && !items.length) ? "exam" : type,
    issued_at: todayIso(),
    validity_in_days: values.validity_in_days ?? null,
    observations: values.observations ?? null,
    control_regime: values.control_regime ?? "none",
    mapa_number: values.mapa_number ?? context.vet.mapa,
    mapa_registration_snapshot: context.vet.mapa,
    // Preview of the sequence number: the real one is allocated atomically when the prescription is issued.
    mapa_prescription_identifier:
      values.control_regime === "manipulated_controlled_mapa" && context.vet.mapa
        ? `${context.vet.mapa}/#####/${String(new Date().getFullYear()).slice(-2)}`
        : null,
    manipulated_payload: type === "manipulated" ? values.manipulated_payload : null,
    medical_record_id: context.medicalRecordId,
    issuer_address: context.vet.address,
    buyer_mode: values.buyer_mode ?? "blank",
    buyer_snapshot: values.buyer_snapshot ?? null,
    items: items.map((i) => ({
      medication_name: i.medication_name,
      medication_concentration: i.medication_concentration || null,
      presentation: i.presentation || null,
      pharmaceutical_form: i.pharmaceutical_form || null,
      administration_route: i.administration_route || null,
      posology_mode: i.posology_mode,
      free_text_posology: i.free_text_posology || null,
      dose_amount: i.dose_amount || null,
      dose_unit: i.dose_unit || null,
      frequency_value: i.frequency_value || null,
      frequency_unit: i.frequency_unit || null,
      duration_value: i.duration_value || null,
      duration_unit: i.duration_unit || null,
    })),
    exam_items: examItems.map((e) => ({ exam_name: e.exam_name, instructions: e.instructions ?? null })),
    pet: context.pet,
    vet: {
      name: context.vet.name,
      crmvForPrint: context.vet.crmvForPrint,
      phone: context.vet.phone,
      address: context.vet.address,
    },
    clinic: { name: context.clinic.name },
  };
  return buildPrescriptionDocumentData(record);
}

export const buildDefaultPrescriptionItem = (): PrescriptionItemInput => ({
  medication_id: "",
  medication_name: "",
  medication_concentration: "",
  pharmaceutical_form: "",
  administration_route: "",
  presentation: "",
  posology_mode: "automatic",
  free_text_posology: "",
  dose_amount: "",
  dose_unit: "",
  frequency_value: "",
  frequency_unit: "",
  duration_value: "",
  duration_unit: "",
});
